import { useCallback, useRef } from "react";

import { NOTIFICATION_KEYS } from "../constants/common";
import { useNotification } from "../context/NotificationContext";
import useAuth from "../hooks/useAuth";
import studentServices from "../services/studentServices";
import { useNavigateWithLang } from "../utils/navigation";

export const useHandleNotificationClick = () => {
  const { markNotificationAsRead } = useNotification();
  const navigate = useNavigateWithLang();
  const user = useAuth();
  const role = user?.role?.toLowerCase() || "student";
  const studentCodeSolutionsModalRef = useRef();
  const studentLevelProjectModalRef = useRef();

  const openStudentCodeSolutionsModal = (model_id, action_by) =>
    studentCodeSolutionsModalRef.current?.openModal(model_id, action_by);

  const openStudentLevelProjectModal = (model_id, action_by) =>
    studentLevelProjectModalRef.current?.openModal(model_id, action_by);

  const handleClick = useCallback(
    async (notification) => {
      const { key, model_id, read_at, id, notification_id, action_by } = notification;

      if (!read_at) {
        await markNotificationAsRead(notification_id || id);
      }

      let finalLink = null;

      switch (key) {
        case NOTIFICATION_KEYS.RESET_QUESTIONS:
          finalLink = `/${role}/learning-area/lesson-questions/${model_id}`;
          break;

        case NOTIFICATION_KEYS.RESET_LESSON:
          finalLink = `/${role}/learning-area/levels-project/${model_id}`;
          break;

        case NOTIFICATION_KEYS.ASSIGN_CLASSROOM:
          finalLink = `/${role}/my-classrooms/${model_id}`;
          break;

        case NOTIFICATION_KEYS.RESUBMITTED_PROJECT:
          openStudentCodeSolutionsModal(model_id, action_by);
          return;

        case NOTIFICATION_KEYS.RESUBMITTED_LEVEL_PROJECT: {
          openStudentLevelProjectModal(model_id, action_by);
          return;
        }
        case NOTIFICATION_KEYS.APPROVED_LEVEL_PROJECT:
        case NOTIFICATION_KEYS.REJECTED_LEVEL_PROJECT: {
          const response = await studentServices.getStudentProject(model_id);
          finalLink = `/${role}/learning-area/levels-project/${response.lesson_id}`;

          navigate(finalLink);
          return;
        }

        case NOTIFICATION_KEYS.APPROVED_PROJECT:
        case NOTIFICATION_KEYS.REJECTED_PROJECT: {
          const response = await studentServices.getStudentProject(model_id);
          if (response.is_graduation) {
            finalLink = `/${role}/coding-area/graduation/${response.lesson_id}`;
          } else if (response.lesson_id && !response.lesson_project_id) {
            finalLink = `/${role}/coding-area/code/${response.lesson_id}`;
          } else if (response.lesson_project_id) {
            finalLink = `/${role}/coding-area/challenge/${response.lesson_project_id}`;
          } else {
            finalLink = `/${role}/coding-area`;
          }

          navigate(finalLink, {}, { fromNotification: true, notificationPayload: response });
          return;
        }

        default:
          navigate(`/${role}/notifications-center`);
          return;
      }

      navigate(finalLink || `/${role}/notifications-center`);
    },
    [markNotificationAsRead, navigate, role]
  );

  return { handleClick, studentCodeSolutionsModalRef, studentLevelProjectModalRef };
};
